import React, { useState, useEffect, useCallback } from 'react';
import { externalApi } from '../../services/api';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Warning, CheckCircle, XCircle } from '@phosphor-icons/react';

export default function ScreenerTab() {
  const [symbol, setSymbol] = useState('RELIANCE');
  const [query, setQuery] = useState('RELIANCE');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (s) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await externalApi.screener(s);
      if (resp.data.error) setError(resp.data.error);
      setData(resp.data);
    } catch (e) {
      console.error('screener fetch failed', e);
      setError('Failed to fetch Screener.in data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(symbol); }, [symbol, fetchData]);

  return (
    <div data-testid="screener-tab-content">
      <div className="flex gap-2 mb-6 max-w-md">
        <Input
          placeholder="Enter NSE symbol (e.g. RELIANCE, TCS)"
          value={query}
          onChange={(e) => setQuery(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && setSymbol(query.trim())}
          className="bg-[#0A0B0E] border-white/20 text-white"
          data-testid="screener-input"
        />
        <Button
          onClick={() => setSymbol(query.trim())}
          className="bg-[#2563EB] hover:bg-[#1D4ED8]"
          data-testid="screener-fetch-btn"
        >
          Fetch
        </Button>
      </div>

      {loading && <div className="text-[#64748B] text-sm">Loading fundamentals…</div>}
      {error && (
        <Card className="p-4 bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444] text-sm mb-4">
          <Warning size={16} weight="fill" className="inline mr-2" />
          {error}
        </Card>
      )}

      {data && !loading && !error && <ScreenerContent data={data} />}
    </div>
  );
}

function ScreenerContent({ data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-3">
        <h2 className="text-2xl font-bold text-white" data-testid="screener-company-name">{data.name}</h2>
        {data.about && <p className="text-sm text-[#94A3B8] mt-2 max-w-3xl">{data.about}</p>}
        {data.url && (
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#2563EB] hover:underline mt-1 inline-block"
          >
            View full report on Screener.in ↗
          </a>
        )}
      </div>

      <RatiosGrid ratios={data.ratios} />
      <ProsConsList pros={data.pros} cons={data.cons} />
      {data.quarterly?.length > 0 && <QuarterlyTables quarterly={data.quarterly} />}
    </div>
  );
}

function RatiosGrid({ ratios }) {
  const entries = Object.entries(ratios || {});
  return (
    <div className="lg:col-span-2">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Key Ratios</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {entries.map(([name, value]) => (
          <div key={name} className="p-3 border border-white/10 bg-white/[0.02]" data-testid={`ratio-${name.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="text-[11px] text-[#64748B] uppercase tracking-wide">{name}</div>
            <div className="text-sm font-mono text-white mt-1">{value}</div>
          </div>
        ))}
        {entries.length === 0 && (
          <div className="col-span-full text-[#64748B] text-sm">No ratios available.</div>
        )}
      </div>
    </div>
  );
}

function ProsConsList({ pros = [], cons = [] }) {
  return (
    <div className="space-y-4">
      <BulletList
        items={pros}
        label="Pros"
        labelColor="text-[#10B981]"
        Icon={CheckCircle}
        iconColor="text-[#10B981]"
      />
      <BulletList
        items={cons}
        label="Cons"
        labelColor="text-[#EF4444]"
        Icon={XCircle}
        iconColor="text-[#EF4444]"
      />
    </div>
  );
}

function BulletList({ items, label, labelColor, Icon, iconColor }) {
  return (
    <div>
      <p className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${labelColor}`}>{label}</p>
      <ul className="space-y-1.5">
        {items.length === 0 && <li className="text-[#64748B] text-xs">None listed.</li>}
        {items.map((text) => (
          <li key={text} className="text-xs text-[#94A3B8] flex items-start gap-1.5">
            <Icon size={14} weight="fill" className={`${iconColor} mt-0.5 shrink-0`} />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuarterlyTables({ quarterly }) {
  return (
    <div className="lg:col-span-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Quarterly Snapshot</p>
      <div className="space-y-3">
        {quarterly.map((q) => (
          <div key={q.metric} className="border border-white/10 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left py-2 px-3 text-white font-medium">{q.metric}</th>
                  {q.data.map((d) => (
                    <th key={d.period} className="text-right py-2 px-3 text-[#94A3B8] font-normal">{d.period}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-3 text-[#64748B]">Value</td>
                  {q.data.map((d) => (
                    <td key={`${q.metric}-${d.period}`} className="text-right py-2 px-3 text-white font-mono">{d.value}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
